import { ChangeDetectionStrategy, Component, ElementRef, ViewChild } from '@angular/core';
import { AgFilterComponent } from '../ag-filter.component';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { IAfterGuiAttachedParams, IDoesFilterPassParams } from 'ag-grid-community';
import { MatChipGrid, MatChipInput, MatChipInputEvent, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { GxInputDirective } from '../../../../directives/gxinput.directive';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatFormField } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-ag-chip-filter',
    templateUrl: './ag-chip-filter.component.html',
    styleUrls: ['./ag-chip-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
  imports: [
    MatFormField,
    MatChipGrid,
    MatChipRow,
    MatChipRemove,
    MatIcon,
    MatInput,
    MatChipInput,
    GxInputDirective,
    TranslateModule,
  ],
})
export class AgChipFilterComponent extends AgFilterComponent {
  // Some store codes may purposefully contain whitespace so the only viable separator triggers are:
  //   A) the comma (which is also the URL value separator)
  //   B) the enter (when "confirming" the transition from a text into a chip).
  // Related to the `pasteIntoStringArrayFilter` fn.
  // Fixes BLS-3064.
  readonly separatorKeysCodes: number[] = [COMMA, ENTER];

  protected filterState: string[] = [];

  @ViewChild('input') input: ElementRef;

  constructor() {
    super();
  }

  afterGuiAttached(params?: IAfterGuiAttachedParams) {
    setTimeout(() => this.input.nativeElement.focus());
  }

  override get filterValue(): string[] {
    return this.filterState;
  }

  override setModel(model: string[]) {
    this.filterState = model ? model : [];
    this.updateFilter();
  }

  override doesFilterPass(params: IDoesFilterPassParams): boolean {
    if (!this.isFilterActive()) {
      return true; // early return, filter is inactive
    }

    // get the value for this filter's column for the provided row data
    // https://www.ag-grid.com/angular-data-grid/component-floating-filter/
    // see Custom Filter And Custom Floating Filter for reference

    const { node } = params;
    const value = this.params.getValue(node)

    // check for callback in filter params, if not present, define default
    const filterCb: (filterValue: any, cellValue: any) => boolean = this.params?.filterCb ?
        this.params.filterCb :
        (filterValues: string[], cellValue: string) => {
          const trimmedCellValue = cellValue.trim();
          return filterValues.some(filterValue => trimmedCellValue.includes(filterValue));
        };

    return value && // check if we have a row value
        filterCb(this.filterValue, value);
  }

  handleReset() {
    this.filterState = [];
    this.updateFilter();
  }

  /**
   * Adds a new value (a new filtering value) to a string-array-filter
   * (e.g. the store-code-filter = array of strings).
   * @param event
   */
  addToStringArrayFilter(event: MatChipInputEvent) {
    if ((event.value || '').trim()) {
      if (!this.filterValue.includes(event.value.trim())) {
        this.filterState.push(event.value.trim());
      }
    }

    if (event.input) {
      // reset the input-field text
      event.input.value = '';
    }
  }

  /**
   * Interprets the data that was pasted into the string-array-filter of a column and adds filters.
   * @param event Paste event
   */
  pasteIntoStringArrayFilter(event: ClipboardEvent) {
    event.preventDefault();

    const pastedText = event.clipboardData.getData('Text').trim();

    if (!pastedText) {
      return;
    }

    pastedText
        // Linked to the `separatorKeysCodes` of the `ChipListFilterComponent`.
        // Some pasted input may purposefully contain whitespace so the only viable separators
        // are the comma and the enter (=newline).
        // Fixes BLS-3064.
        .split(/[,\n]+/) //Splits it on (one or multiple) commas or newlines
        .forEach((value) => {
          if (value.trim()) {
            if (!this.filterValue.includes(value.trim())) {
              this.filterState.push(value.trim());
            }
          }
        });
  }

  /**
   * Removes a value (a filtering value) from a string-array-filter (e.g. the store-code-filter = array of strings).
   * @param value Value that should be removed from filtered values.
   */
  removeFromStringArrayFilter(value: string) {
    const index = this.filterValue.indexOf(value);
    if (index >= 0) {
      this.filterState.splice(index, 1);
    }
  }


}
